export enum ChannelPermission {
  /**
   * Use for chat questions during the show.
   */
  askQuestions = "askQuestions",
  interactInContests = "interactInContests",
  interactInThreads = "interactInThreads",
  produceShows = "produceShows",
  manageShows = "manageShows",
  manageContests = "manageContests",
  manageThreads = "manageThreads",
  manageChannel = "manageChannel",
  manageOffers = "manageOffers",

  // Feature gating
  chat = "chat",
  raffleJoin = "raffleJoin",
  quizVote = "quizVote",
  showJoinStage = "showJoinStage",
  claimOffers = "claimOffers",
}

export type IChannelPermissionsDescriptor = Record<ChannelPermission, boolean>;

/**
 * Non-sequential permissions bitmask for channel roles.
 *
 * ------------------------------------------------------------
 * ******** DON'T CHANGE THE VALUES OF THE PERMISSIONS ********
 * ------------------------------------------------------------
 *
 * If you need to add a new permission, add the value to the end of the record,
 * you may change the key name. If you delete a permission, change the key name
 * to be `_DELETED_${key}` but keep its value for BACKWARDS COMPATIBILITY.
 */
export const channelPermissionsMap: Record<ChannelPermission, number> =
  Object.freeze({
    [ChannelPermission.askQuestions]: 1 << 1,
    [ChannelPermission.interactInContests]: 1 << 2,
    [ChannelPermission.interactInThreads]: 1 << 3,
    [ChannelPermission.produceShows]: 1 << 4,
    [ChannelPermission.manageShows]: 1 << 5,
    [ChannelPermission.manageContests]: 1 << 6,
    [ChannelPermission.manageThreads]: 1 << 7,
    [ChannelPermission.manageChannel]: 1 << 8,
    [ChannelPermission.manageOffers]: 1 << 13,

    // Feature gating
    [ChannelPermission.chat]: 1 << 9,
    [ChannelPermission.raffleJoin]: 1 << 10,
    [ChannelPermission.quizVote]: 1 << 11,
    [ChannelPermission.showJoinStage]: 1 << 12,
    [ChannelPermission.claimOffers]: 1 << 14,
  });

export const channelPermissionsMapReverse: Record<number, ChannelPermission> =
  Object.freeze(
    Object.entries(channelPermissionsMap).reduce(
      (acc, [key, value]) => {
        acc[value] = key as ChannelPermission;
        return acc;
      },
      {} as Record<number, ChannelPermission>,
    ),
  );

/**
 * To differentiate between roles with same permission set, we can offset permission mask with numberOfPermissions.
 *
 * ----------------------------------------------------------------------------------
 * ******** IF YOU ADD ADDITIONAL CHANNEL PERMISSIONS MAP, YOU WILL NEED TO UPDATE EVERY ********
 * ******** permissions_mask IN DATABASE WITH mask = mask << (numberOfPermissions * permission_mask_level) ********
 * ----------------------------------------------------------------------------------
 */

// IMPORTANT: Increment this constant only alongside a DB migration that re-encodes
// channel_memberships and web_three_token_criteria rows with permission_mask_level > 0.
// Do NOT derive dynamically from Object.keys() — silent changes corrupt level-shifted masks.
export const numberOfPermissions = 14;

export const allFanPermissions = addPermissionsMasks([
  channelPermissionsMap[ChannelPermission.askQuestions],
  channelPermissionsMap[ChannelPermission.interactInContests],
  channelPermissionsMap[ChannelPermission.interactInThreads],

  channelPermissionsMap[ChannelPermission.chat],
  channelPermissionsMap[ChannelPermission.raffleJoin],
  channelPermissionsMap[ChannelPermission.quizVote],
  channelPermissionsMap[ChannelPermission.showJoinStage],
  channelPermissionsMap[ChannelPermission.claimOffers],
]);

export const allPermissions = addPermissionsMasks(
  Object.values(channelPermissionsMap),
);

export enum ObjectWithPermission {
  show = "show",
  quiz = "quiz",
  raffle = "raffle",
  thread = "thread",
  offer = "offer",
}

export const gateablePermissions: Record<
  ObjectWithPermission,
  ChannelPermission[]
> = Object.freeze({
  [ObjectWithPermission.show]: [
    ChannelPermission.askQuestions,
    ChannelPermission.quizVote,
    ChannelPermission.raffleJoin,
    ChannelPermission.chat,
    ChannelPermission.showJoinStage,
  ],
  [ObjectWithPermission.quiz]: [],
  [ObjectWithPermission.raffle]: [],
  [ObjectWithPermission.thread]: [],
  [ObjectWithPermission.offer]: [ChannelPermission.claimOffers],
});

export function hasPerPermissionGating(
  objectType: ObjectWithPermission,
): boolean {
  return gateablePermissions[objectType].length > 1;
}

export const defaultChannelPermissions: IChannelPermissionsDescriptor = {
  [ChannelPermission.askQuestions]: false,
  [ChannelPermission.interactInContests]: false,
  [ChannelPermission.interactInThreads]: false,
  [ChannelPermission.produceShows]: false,
  [ChannelPermission.manageShows]: false,
  [ChannelPermission.manageContests]: false,
  [ChannelPermission.manageThreads]: false,
  [ChannelPermission.manageChannel]: false,
  [ChannelPermission.manageOffers]: false,

  // Feature gating
  [ChannelPermission.chat]: false,
  [ChannelPermission.raffleJoin]: false,
  [ChannelPermission.quizVote]: false,
  [ChannelPermission.showJoinStage]: false,
  [ChannelPermission.claimOffers]: false,
};

export function generateMaskForLevel(mask: number, level: number = 0): number {
  let result = mask;

  for (let i = 0; i < level + 1; i++) {
    result = result | (mask << (i * numberOfPermissions));
  }

  return result;
}

export function addPermissionsMasks(masks: number[]): number {
  return masks.reduce((acc, mask) => acc | mask, 0);
}

export function hasPermission(
  permissionMask: number,
  requiredPermission: number | ChannelPermission,
): boolean {
  const requiredPermissionMask =
    typeof requiredPermission === "number"
      ? requiredPermission
      : channelPermissionsMap[requiredPermission];

  return !!(permissionMask & requiredPermissionMask);
}

export function hasPermissionWithLevel(
  permission: ChannelPermission,
  mask: number,
  level: number = 0,
): boolean {
  return !!(
    mask &
    (channelPermissionsMap[permission] << (level * numberOfPermissions))
  );
}

export function getChannelPermissionsForMask(
  mask: number,
  level: number = 0,
): IChannelPermissionsDescriptor {
  const permissions = {} as Record<ChannelPermission, boolean>;

  for (const permission in channelPermissionsMap) {
    permissions[permission as ChannelPermission] = hasPermissionWithLevel(
      permission as ChannelPermission,
      mask,
      level,
    );
  }

  return permissions;
}
